import "react-datepicker/dist/react-datepicker.css";
import { useEffect, useMemo, useState } from "react";
import { ReportApi } from "src/services/api/ReportApi";
import {
  ColDef,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  LoadSuccessParams,
  GridApi,
  SizeColumnsToContentStrategy,
} from "ag-grid-enterprise";
import { AgBarSeriesOptions, AgChartOptions } from "ag-charts-enterprise";
import { GiftCardTransactionDetailModel } from "src/models/GiftCardTransactionDetailModel";
import useSidebar from "src/hooks/useSidebar";
import useReference from "src/hooks/useReference";
import useTheme from "src/hooks/useTheme";
import GiftCardTransactionDetailSummaryData from "./GiftCardTransactionDetailSummaryData";
import { AgCharts } from "ag-charts-react";
import { GiftCardTransactionDetailSummaryModel } from "src/models/GiftCardTransactionDetailSummaryModel";
import BaseGrid from "../../BaseGrid";
import ValidationService from "src/services/ValidationService";
import { CardAmountByDate } from "src/models/CardAmountByDate";
import useReport from "src/hooks/useReport";
import { downloadFile } from "src/utils/DownloadFile";
import SearchCriteria from "src/components/SearchCriteria";
import LoadingComponent from "src/components/LoadingComponent";

const GiftCardTransactionDetail = () => {
  const { setIsOpen } = useSidebar();
  const { appSettings } = useReference();
  const { theme } = useTheme();  
  const {
    reportInputs,
    setExceedsMaxDateRange,
    mapReportRequest,
    currencyFormatter,
    defaultColDef,
    getContextMenuItems,
    cardNumberFormatter
  } = useReport();
  const [summaryDetail, setSummaryDetail] =
    useState<GiftCardTransactionDetailSummaryModel>(
      {} as GiftCardTransactionDetailSummaryModel
    );
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [gridReady, setGridReady] = useState(false);
  const [dataSource, setDataSource] = useState<IServerSideDatasource>();
  const [gridApi, setGridApi] = useState<GridApi>();
  const [isDownload, setIsDownload] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const nextOptions = { ...options };
    nextOptions.theme = theme === "dark" ? "ag-material-dark" : "ag-material";
    setOptions(nextOptions);
  }, [theme]);

  const handleViewReport = () => {
    if (
      ValidationService.isValidDateRange(
        reportInputs?.fromDate,
        reportInputs?.toDate,
        appSettings.dateRangeMonthsLimit_View
      )
    ) {
      setIsDownload(false);
      setExceedsMaxDateRange(false);
      setGridReady(true);
      setSummaryDetail({} as GiftCardTransactionDetailSummaryModel);
      setChartData([]);
      setDataSource(createServerSideDatasource());
    } else {
      setExceedsMaxDateRange(true);
    }
  };

  const handleDownloadReport = () => {
    if (
      ValidationService.isValidDateRange(
        reportInputs?.fromDate,
        reportInputs?.toDate,
        appSettings.dateRangeMonthsLimit_Download
      )
    ) {
      setIsDownload(true);
      setIsLoading(true);
      setExceedsMaxDateRange(false);
      ReportApi.exportGiftCardTransactionDetails(mapReportRequest(null))
        .then((res) => {
          downloadFile(GiftCardTransactionDetail.displayName, res.data);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setExceedsMaxDateRange(true);
    }
  };

  const [colDefs] = useState<ColDef<GiftCardTransactionDetailModel>[]>([
    { field: "transactionId" },
    { field: "clientReferenceNumber", cellDataType: "text" },
    { field: "carrierNumber", cellDataType: "text" },
    { field: "merchantGroup" },
    { field: "description" },
    { field: "cardNumber", valueFormatter: cardNumberFormatter },
    { field: "externalId" },
    { field: "cardType" },
    { field: "campaign", cellDataType: "text" },
    {
      headerName: "Expiration Period",
      field: "expirationPeriodDisplay",
      sortable: false,
      cellDataType: "text",
    },
    {
      headerName: "Card Amount",
      field: "fundAmount",
      aggFunc: "sum",
      valueGetter: (params) => params.data?.fundAmount,
      valueFormatter: currencyFormatter,
      cellStyle: { textAlign: "right", paddingRight: "50px" },
    },
    {
      field: "propertyFee",
      aggFunc: "sum",
      valueGetter: (params) => params.data?.propertyFee,
      valueFormatter: currencyFormatter,
      cellStyle: { textAlign: "right", paddingRight: "50px" },
    },
    {
      headerName: "Item Total",
      field: "amount",
      aggFunc: "sum",
      valueGetter: (params) => params.data?.amount,
      valueFormatter: currencyFormatter,
      cellStyle: { textAlign: "right", paddingRight: "50px" },
    },
    { headerName: "Type", field: "paymentType" },
    { headerName: "CSR", field: "csr" },
    { field: "date" },
    { field: "clientOrderDate" },
    { field: "salesChannel" },
    { field: "clientTrackingId", cellDataType: "text" },
  ]);

  const autoSizeStrategy = useMemo<SizeColumnsToContentStrategy>(() => {
    return {
      type: "fitCellContents",
    };
  }, []);

  const createServerSideDatasource: () => IServerSideDatasource = () => {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        getReportData(params);
      },
    };
  };

  const getReportData = (params: IServerSideGetRowsParams | null) => {
    setIsOpen(false);
    ReportApi.getGiftCardTransactionDetails(mapReportRequest(params))
      .then((res) => {
        const result = res.data;
        result?.rows.map(
          (x: GiftCardTransactionDetailModel) => (
            (x.date = new Date(x.date).toLocaleDateString()),
            (x.clientOrderDate = x.clientOrderDate
              ? new Date(x.clientOrderDate).toLocaleDateString()
              : undefined)
          )
        );
        const successParams: LoadSuccessParams = {
          rowData: result.rows,
          rowCount: result.totalCount,
        };
        if ((result?.rows?.length ?? 0) > 0) {
          params?.api?.hideOverlay();
          params?.success(successParams);
        } else {
          params?.api?.showNoRowsOverlay();
          params?.success({ rowData: [], rowCount: 0 });
        }
        setSummaryDetail(result.summaryDetail);
        const cd = mapChartData(result.summaryDetail);
        setChartData(cd);
        setOptions((prevOptions) => ({
          ...prevOptions,
          data: cd,
        }));
      })
      .catch(() => {
        params?.fail();
      });
  };

  const mapChartData = (data: GiftCardTransactionDetailSummaryModel) => {
    const cardAmountsByDate: CardAmountByDate[] = [];
    data.cardAmounts.map((ca) => {
      const cardAmount: CardAmountByDate = {
        amount: ca.amount,
        date: new Date(ca.date).toLocaleDateString(),
      };
      cardAmountsByDate.push(cardAmount);
    });
    return cardAmountsByDate;
  };

  const [options, setOptions] = useState<AgChartOptions>({
    title: {
      text: "Card Amount Total By Date",
    },
    // Data: Data to be displayed in the chart
    data: chartData as ChartData[],
    // Series: Defines which chart type and data to use
    series: [
      {
        type: "bar",
        xKey: "date",
        yKey: "amount",
      } as AgBarSeriesOptions,
    ],
    theme: theme === "dark" ? "ag-material-dark" : "ag-material",
  });  

  return (
    <>
      <div className="report-container">
        <div className="report-header">
          <h5 className="mb-0">Gift Card Transaction Detail</h5>
          {(reportInputs?.programId ?? 0) > 0 &&
            reportInputs?.fromDate &&
            reportInputs?.toDate && (
              <div className="report-header-date">{`${reportInputs?.fromDate.toLocaleDateString()} - ${reportInputs?.toDate.toLocaleDateString()}`}</div>
            )}
        </div>
        <div className="blocks">
          <SearchCriteria
            usesLocations={true}
            usesUsers={true}
            usesCampaigns={false}
            handleViewReport={handleViewReport}
            handleDownloadReport={handleDownloadReport}
          />
          {(chartData?.length ?? 0) > 0 && !isDownload && (
            <div className="report-summary">
              <GiftCardTransactionDetailSummaryData
                summaryDetail={summaryDetail}
              />
            </div>
          )}
          {(chartData?.length ?? 0) > 0 && !isDownload && (
            <div className="report-chart">
              <AgCharts options={options} />
            </div>
          )}
        </div>
        {isDownload && isLoading ? (
          <LoadingComponent />
        ) : (
          !isDownload && (
            <BaseGrid
              colDefs={colDefs}
              defaultColDef={defaultColDef}
              getData={getReportData}
              isGridReady={gridReady}
              reportInputs={reportInputs}
              dataSource={dataSource}
              gridApi={gridApi}
              setGridApi={setGridApi}
              getContextMenuItems={getContextMenuItems}
              autoSizeStrategy={autoSizeStrategy}
            />
          )
        )}
      </div>
    </>
  );
};
GiftCardTransactionDetail.displayName = "GiftCardTransactionDetail";

export default GiftCardTransactionDetail;
